import { useState, useEffect } from "react";
import AgentsService from "../../services/AgentsService/AgentsService.ts";
import { useAuth } from "../../provider/AuthProvider.tsx";
import SelectGroup from "../../components/Forms/SelectGroup/SelectGroup.tsx";
import { normalizeAgentProperties } from "../../utils/NormalizationUtil.ts";
import { Agent } from "../../types/agent.ts";

interface RemoveAgentPopupProps {
  officeName: string;
  agentsIds: string[];
  onClose: () => void;
  onSave: (updatedAgents: string[]) => void | Promise<void>;
}

const RemoveAgentPopup = ({
  onClose,
  onSave,
  agentsIds,
  officeName,
}: RemoveAgentPopupProps) => {
  const { jwt } = useAuth();

  const [agents, setAgents] = useState<Record<string, string>>({});
  const [selectedAgent, setSelectedAgent] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let mounted = true;

    const fetchAgents = async () => {
      try {
        const agentsData = await Promise.all(
          agentsIds.map(async (id): Promise<Agent> => {
            return normalizeAgentProperties(
              (await AgentsService.getAgentById(jwt, id)) as object
            );
          })
        );

        const mappedAgents = agentsData.reduce((acc, agent) => {
          acc[String(agent.agentID)] = agent.agentName;
          return acc;
        }, {} as Record<string, string>);

        if (mounted) setAgents(mappedAgents);
      } catch {
        if (mounted) {
          setError("Грешка при зареждането на агенти.");
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchAgents();

    return () => {
      mounted = false;
    };
  }, [jwt, agentsIds]);

  const handleRemove = async () => {
    if (!selectedAgent || saving) return;

    try {
      setSaving(true);
      setError("");

      await onSave(agentsIds.filter((id) => String(id) !== selectedAgent));
    } catch {
      setError("Неуспешно премахване на агент.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-[2px]">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="remove-agent-title"
        className="dark:bg-boxdark w-full max-w-md overflow-hidden rounded-xl bg-white shadow-2xl"
      >
        {/* Header */}
        <div className="border-stroke dark:border-strokedark flex items-center justify-between border-b px-5 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-rose-50 text-rose-600 dark:bg-rose-900/20 dark:text-rose-400">
              <i className="fa-kit fa-solid-minus-user text-lg" />
            </div>

            <div>
              <h3
                id="remove-agent-title"
                className="text-base font-semibold text-black dark:text-white"
              >
                Премахване на агент
              </h3>
              <p className="text-bodydark2 text-xs">
                Управление на агенти към офис
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            aria-label="Затвори"
            className="text-bodydark2 dark:hover:bg-meta-4 rounded-lg p-2 transition hover:bg-gray-100 hover:text-black disabled:opacity-50 dark:hover:text-white"
          >
            <i className="fa-solid fa-xmark" />
          </button>
        </div>

        {/* Content */}
        <div className="space-y-5 p-5">
          <div className="border-stroke dark:border-strokedark dark:bg-meta-4 rounded-lg border bg-gray-50 px-4 py-3">
            <p className="text-bodydark2 mb-1 text-[10px] font-semibold uppercase tracking-wider">
              Избран офис
            </p>
            <p className="text-sm font-semibold text-black dark:text-white">
              {officeName}
            </p>
          </div>

          <div>
            <label className="mb-2 block text-xs font-semibold text-black dark:text-white">
              Агент за премахване
            </label>

            <SelectGroup
              value={selectedAgent}
              placeholder={loading ? "Зареждане..." : "Изберете агент"}
              options={agents}
              onChange={(value) => {
                setSelectedAgent(value);
                setError("");
              }}
            />

            {selectedAgent ? (
              <div className="mt-3 rounded-lg border border-rose-200 bg-rose-50 p-3 dark:border-rose-900 dark:bg-rose-900/20">
                <div className="flex items-start gap-2">
                  <i className="fa-solid fa-circle-exclamation mt-0.5 text-rose-600 dark:text-rose-400" />
                  <div className="text-xs text-rose-800 dark:text-rose-300">
                    <p className="font-semibold">Предстои премахване</p>
                    <p className="mt-1">
                      <strong>{agents[selectedAgent]}</strong> ще бъде премахнат
                      от избрания офис.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-bodydark2 mt-2 text-xs">
                Изберете агент, който желаете да премахнете от офиса.
              </p>
            )}

            {error && (
              <p role="alert" className="text-danger mt-3 text-xs">
                {error}
              </p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="border-stroke dark:border-strokedark dark:bg-meta-4 flex items-center justify-end gap-2 border-t bg-gray-50 px-5 py-4">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="border-stroke dark:border-strokedark dark:bg-boxdark rounded-lg border bg-white px-4 py-2 text-xs font-semibold text-black transition hover:bg-gray-100 disabled:opacity-50 dark:text-white dark:hover:bg-opacity-80"
          >
            Отказ
          </button>

          <button
            type="button"
            onClick={handleRemove}
            disabled={!selectedAgent || loading || saving}
            className="flex items-center gap-2 rounded-lg bg-rose-600 px-4 py-2 text-xs font-semibold text-white transition hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {saving ? (
              <i className="fa-solid fa-spinner fa-spin" />
            ) : (
              <i className="fa-kit fa-solid-minus-user" />
            )}
            {saving ? "Проверка..." : "Премахване"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RemoveAgentPopup;
